/**
 * 구매 화면(`/shop/{code}`) 4종의 데이터.
 *
 * 역할 분담 (SITEMAP_PLAN §16.1):
 *   `/products/{code}` — **이 제품이 무엇인가.** 랜딩 + 정본 상세. 색인 대상.
 *   `/shop/{code}`     — **이걸 산다.** 여기.
 * 판별식: *이미 사기로 마음먹은 사람에게도 필요한가?* 예면 여기, 아니오면 Products.
 *
 * 그래서 이 표에는 **살 때 확인하는 것만** 있다 — 갤러리(맞는 물건인지), 표시명, 그리고
 * "내 보드에 들어가나 / 전원이 되나"에 답하는 서너 줄. 사양 전체표·호환 장비·본문 이미지·
 * 설치 절차는 전부 `productDetail.config.ts` 로 갔다 (2026-09-10, §17).
 *
 * 표시명·워드마크 규정의 SSOT 는 claudeFiles/judahsWeb/PRODUCT_NAMES.md.
 */

import { DetailImageType, SpecRowType } from '@components/systems/monolith/MonolithBands'

// koinos pls 8 — 제품 페이지(`/products/pls8`)와 같은 촬영분을 쓴다. 다른 페이지라 겹쳐도 된다
// (§2 "같은 사진 두 번 금지"는 한 페이지 안의 규칙).
import pls8Gal1 from '@public/images/monolith/koinos8-angle-full.png'
import pls8Gal2 from '@public/images/monolith/koinos8-top-front-screen-m7.png'
import pls8Gal3 from '@public/images/monolith/koinos8-top-angle-screen-m54.png'
import pls8Gal4 from '@public/images/monolith/koinos8-rear-front.png'
import pls8Gal5 from '@public/images/monolith/koinos8-detail-switches.png'
import pls8Gal6 from '@public/images/monolith/koinos8-detail-lettering.png'

// kedem 6
import kdm6Gal1 from '@public/images/monolith/kedem6-top.webp'
import kdm6Gal2 from '@public/images/pages/shop/kedem6/gallery/kedem6-gallery2.jpg'
import kdm6Gal3 from '@public/images/pages/shop/kedem6/gallery/kedem6-gallery3.jpg'
import kdm6Gal4 from '@public/images/pages/shop/kedem6/gallery/kedem6-gallery4.jpg'
import kdm6Gal5 from '@public/images/pages/shop/kedem6/gallery/kedem6-gallery5.jpg'
import kdm6Gal6 from '@public/images/pages/shop/kedem6/gallery/kedem6-gallery6.jpg'

// kedem 4
import kdm4Gal1 from '@public/images/monolith/kedem4-top.webp'
import kdm4Gal2 from '@public/images/pages/shop/kedem4/gallery/kedem4-gallery2.jpg'
import kdm4Gal3 from '@public/images/pages/shop/kedem4/gallery/kedem4-gallery3.jpg'
import kdm4Gal4 from '@public/images/pages/shop/kedem4/gallery/kedem4-gallery4.jpg'
import kdm4Gal5 from '@public/images/pages/shop/kedem4/gallery/kedem4-gallery5.jpg'
import kdm4Gal6 from '@public/images/pages/shop/kedem4/gallery/kedem4-gallery6.jpg'

// airmidi pog 2
import amp2Gal1 from '@public/images/pages/shop/amp2/gallery/amp2-module-mono.jpg'
import amp2Gal2 from '@public/images/pages/shop/amp2/gallery/amp2-gallery2.jpg'
import amp2Gal3 from '@public/images/pages/shop/amp2/gallery/amp2-gallery3.jpg'

// dual switch
import dsGal2 from '@public/images/pages/shop/dualSwitch/gallery/ds-gallery2.jpg'
import dsGal3 from '@public/images/pages/shop/dualSwitch/gallery/ds-gallery3.jpg'

export interface ShopDetailType {
	/** 제품 코드 = URL(`/shop/{code}` 소문자) · API 식별자 */
	code: string
	/** 표시명 — 본문·SEO·alt 용 (PRODUCT_NAMES §1) */
	name: string
	/** 제목 워드마크 종류. 규정이 없는 제품은 'text' — 본문 서체 표시명 */
	wordmark: 'koinos' | 'kedem' | 'text'
	/** KedemWordmark 의 model. wordmark 가 'kedem' 일 때만 */
	model?: string
	/** 워드마크 아래 한 줄. 제품 페이지와 같은 문장을 쓴다 */
	tagline: string
	/** 갤러리 — 첫 장이 대표컷(JSON-LD·OG 에 쓰는 것과 같은 파일) */
	gallery: DetailImageType[]
	/**
	 * 구매 확인용 서너 줄. **"내가 맞는 걸 사는가"에만 답한다** —
	 * 보드에 들어가는 치수, 전원, 무엇에 붙는지. 전체 사양표는 제품 페이지에 있다.
	 */
	summary: SpecRowType[]
	/**
	 * 페이지 맨 아래에서 제품 페이지로 보내는 한 줄.
	 *
	 * **단정문이어야 한다** (PERSONA §1 규칙 1 — 설득·권유 문형 금지). "더 자세한 내용이
	 * 필요하신가요?" 처럼 묻지 않는다. 저쪽에 **무엇이 있는지 그냥 말하고**, 판단은 읽는 쪽에
	 * 맡긴다(규칙 3 — 손님을 고른다). 그래서 "더 많은 정보" 같은 뭉뚱그린 말 대신 제품마다
	 * 실제로 그쪽에 있는 것을 이름으로 적는다.
	 */
	moreLine: string
	/** `manual.config.ts` 의 코드. 없으면 매뉴얼 참조 행을 그리지 않는다 */
	manualCode?: string
}

export const shopDetailList: ShopDetailType[] = [
	{
		/**
		 * 아직 값이 안 매겨졌다. 구매 패널이 `seasonUsd <= 0` 을 보고 가격 자리를 비우고
		 * Register interest 로 내려앉는다 — 여기 따로 적을 것이 없다.
		 */
		code: 'PLS8',
		name: 'Koinos PLS 8',
		wordmark: 'koinos',
		tagline: 'Your rig. One screen.',
		gallery: [
			{ src: pls8Gal1, alt: 'Koinos PLS 8' },
			{ src: pls8Gal2, alt: 'Koinos PLS 8' },
			{ src: pls8Gal3, alt: 'Koinos PLS 8' },
			{ src: pls8Gal4, alt: 'Koinos PLS 8' },
			{ src: pls8Gal5, alt: 'Koinos PLS 8' },
			{ src: pls8Gal6, alt: 'Koinos PLS 8' },
		],
		moreLine: 'Loops, routing and the screen are on the product page.',
		summary: [
			{ label: 'Loops', value: '8, plus a separate buffered insert' },
			{ label: 'Display', value: '8.88-inch touch' },
			{ label: 'Footswitches', value: '10, plus 8 combinations' },
			// 매뉴얼 Appendix A. 깊이는 안테나를 뺀 값
			{ label: 'Size', value: '290 × 120 × 86 mm' },
			{ label: 'Weight', value: '1.44 kg' },
			{ label: 'Power', value: '9V DC, centre negative, 1 A' },
		],
	},

	{
		code: 'KDM6',
		name: 'Kedem 6',
		wordmark: 'kedem',
		model: '6',
		tagline: 'Six switches. One screen.',
		manualCode: 'KDM6',
		gallery: [
			{ src: kdm6Gal1, alt: 'Kedem 6' },
			{ src: kdm6Gal2, alt: 'Kedem 6' },
			{ src: kdm6Gal3, alt: 'Kedem 6' },
			{ src: kdm6Gal4, alt: 'Kedem 6' },
			{ src: kdm6Gal5, alt: 'Kedem 6' },
			{ src: kdm6Gal6, alt: 'Kedem 6' },
		],
		moreLine: 'Ports, switch actions and the gear it controls are on the product page.',
		summary: [
			{ label: 'Size', value: '190 × 121 × 62 mm' },
			{ label: 'Weight', value: '960 g' },
			{ label: 'Power', value: '9V DC, centre negative, 250 mA' },
		],
	},

	{
		code: 'KDM4',
		name: 'Kedem 4',
		wordmark: 'kedem',
		model: '4',
		tagline: 'Four switches. Same screen.',
		manualCode: 'KDM4',
		gallery: [
			{ src: kdm4Gal1, alt: 'Kedem 4' },
			{ src: kdm4Gal2, alt: 'Kedem 4' },
			{ src: kdm4Gal3, alt: 'Kedem 4' },
			{ src: kdm4Gal4, alt: 'Kedem 4' },
			{ src: kdm4Gal5, alt: 'Kedem 4' },
			{ src: kdm4Gal6, alt: 'Kedem 4' },
		],
		moreLine: 'Ports, switch actions and the gear it controls are on the product page.',
		summary: [
			{ label: 'Size', value: '140 × 121 × 62 mm' },
			{ label: 'Weight', value: '760 g' },
			{ label: 'Power', value: '9V DC, centre negative, 250 mA' },
		],
	},

	{
		code: 'AMP2',
		name: 'AirMIDI POG 2',
		wordmark: 'text',
		tagline: 'The POG2, over the air.',
		gallery: [
			{ src: amp2Gal1, alt: 'AirMIDI POG 2' },
			{ src: amp2Gal2, alt: 'AirMIDI POG 2' },
			{ src: amp2Gal3, alt: 'AirMIDI POG 2' },
		],
		moreLine: 'Fitting, MIDI CC and the channel map are on the product page.',
		summary: [
			{ label: 'Fits', value: 'Electro-Harmonix POG2' },
			{ label: 'Fitting', value: 'Soldered inside the pedal' },
			{ label: 'Option', value: 'We fit it, or you do' },
		],
	},

	{
		code: 'DS',
		name: 'Dual Switch',
		wordmark: 'text',
		tagline: 'Two switches. One TRS cable.',
		gallery: [
			{ src: dsGal2, alt: 'Judahs Dual Switch' },
			{ src: dsGal3, alt: 'Judahs Dual Switch' },
		],
		moreLine:
			'The controllers it fits, and what it adds to each, are on the product page.',
		summary: [
			{ label: 'Switches', value: '2× momentary' },
			{ label: 'Connection', value: '1/4-inch TRS' },
			{ label: 'Works with', value: 'Judahs TRS switch inputs' },
		],
	},
]

/** 코드(대문자)로 찾는다. 없는 코드면 undefined — 라우트가 4종만 걸어 두어 실제로는 나지 않는다. */
export const getShopDetail = (code: string): ShopDetailType | undefined =>
	shopDetailList.find(aDetail => aDetail.code === code.toUpperCase())
